import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Loader2, Mail, KeyRound, ArrowLeft } from 'lucide-react';
import { sendOtp, verifyOtp } from '../api';
import { getErrorMessage } from '../api/NetworkManager';
import { useAuth } from '../context/AuthContext';

type Step = 'yumId' | 'otp';

const RESEND_COOLDOWN_SECONDS = 60;

export function LoginPage() {
  const navigate = useNavigate();
  const { applyAuth, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<Step>('yumId');
  const [yumId, setYumId] = useState('');
  const [otp, setOtp] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const otpInputRef = useRef<HTMLInputElement>(null);

  // If already authenticated, bounce home
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Surface expired-session message
  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      setError('Your session expired. Please sign in again.');
    }
  }, [searchParams]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((n) => Math.max(0, n - 1)), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  // Auto-focus OTP input when entering OTP step
  useEffect(() => {
    if (step === 'otp') otpInputRef.current?.focus();
  }, [step]);

  const normalizedYumId = yumId.trim().toLowerCase();
  const yumIdValid = /^[a-z0-9]{3,20}$/.test(normalizedYumId);

  async function handleSendOtp(e?: React.FormEvent) {
    e?.preventDefault();
    if (!yumIdValid || isSending) return;
    setError(null);
    setIsSending(true);
    try {
      await sendOtp(normalizedYumId);
      setStep('otp');
      setResendIn(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(getErrorMessage(err, "Couldn't send code. Please try again."));
    } finally {
      setIsSending(false);
    }
  }

  async function handleVerifyOtp(e?: React.FormEvent) {
    e?.preventDefault();
    if (otp.length !== 6 || isVerifying) return;
    setError(null);
    setIsVerifying(true);
    try {
      const result = await verifyOtp(normalizedYumId, otp);
      if (result.isNewUser) {
        navigate('/register', {
          state: { registrationToken: result.registrationToken, profile: result.profile },
          replace: true,
        });
        return;
      }
      applyAuth(
        {
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          expiresIn: result.expiresIn,
          tokenType: 'Bearer',
        },
        result.profile,
      );
      navigate('/', { replace: true });
    } catch (err) {
      setError(getErrorMessage(err, 'Invalid or expired code.'));
      setOtp('');
      otpInputRef.current?.focus();
    } finally {
      setIsVerifying(false);
    }
  }

  async function handleResend() {
    if (resendIn > 0 || isSending) return;
    setError(null);
    setIsSending(true);
    try {
      await sendOtp(normalizedYumId);
      setResendIn(RESEND_COOLDOWN_SECONDS);
      setOtp('');
      otpInputRef.current?.focus();
    } catch (err) {
      setError(getErrorMessage(err, "Couldn't resend code."));
    } finally {
      setIsSending(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-surface">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 -top-32 h-[500px] w-[500px] rounded-full bg-gradient-to-br from-orange-400/20 via-red-500/10 to-transparent blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-[500px] w-[500px] rounded-full bg-gradient-to-tl from-purple-600/15 via-red-500/10 to-transparent blur-3xl" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-12">
        <div className="mb-10 flex items-center gap-3">
          <img src="/logo.svg" alt="Byte" className="h-9" />
          <span className="font-display text-lg font-bold tracking-tight text-ink">Byte Hackathon</span>
        </div>

        <div className="rounded-2xl border border-ring/60 bg-surface-raised/80 p-8 shadow-card backdrop-blur-sm">
          {step === 'yumId' ? (
            <form onSubmit={handleSendOtp} className="space-y-5">
              <div>
                <div className="mb-1 inline-flex items-center gap-2 rounded-full border border-ring/60 bg-surface px-2.5 py-0.5 text-[11px] font-medium text-ink-secondary">
                  <Mail className="h-3 w-3" />
                  Sign in
                </div>
                <h1 className="font-display text-2xl font-bold text-ink">Welcome back</h1>
                <p className="mt-1 text-sm text-ink-secondary">
                  Enter your Yum ID. We'll email a one-time code to <strong>yumid@yum.com</strong>.
                </p>
              </div>

              <div>
                <label htmlFor="yumId" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-secondary">
                  Yum ID
                </label>
                <input
                  id="yumId"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="text"
                  required
                  value={yumId}
                  onChange={(e) => setYumId(e.target.value)}
                  placeholder="myd7020"
                  className="input-base w-full"
                />
              </div>

              {error && (
                <div className="rounded-lg border border-status-error/30 bg-status-error/5 px-3 py-2 text-sm text-status-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!yumIdValid || isSending}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 via-red-500 to-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-red-500/20 transition-all hover:shadow-xl hover:shadow-red-500/30 disabled:cursor-not-allowed disabled:opacity-60 active:scale-[0.98]"
              >
                {isSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Send code <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>

              <p className="text-center text-xs text-ink-tertiary">
                By continuing you agree to use your Yum corporate email responsibly.
              </p>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setStep('yumId');
                    setOtp('');
                    setError(null);
                  }}
                  className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-ink-tertiary hover:text-ink-secondary"
                >
                  <ArrowLeft className="h-3 w-3" /> Change Yum ID
                </button>
                <div className="mb-1 inline-flex items-center gap-2 rounded-full border border-ring/60 bg-surface px-2.5 py-0.5 text-[11px] font-medium text-ink-secondary">
                  <KeyRound className="h-3 w-3" />
                  Verify
                </div>
                <h1 className="font-display text-2xl font-bold text-ink">Check your email</h1>
                <p className="mt-1 text-sm text-ink-secondary">
                  We sent a 6-digit code to <strong>{normalizedYumId}@yum.com</strong>. It expires in 10 minutes.
                </p>
              </div>

              <div>
                <label htmlFor="otp" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-secondary">
                  One-time code
                </label>
                <input
                  ref={otpInputRef}
                  id="otp"
                  type="text"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  className="input-base w-full text-center font-mono text-2xl tracking-[0.3em]"
                />
              </div>

              {error && (
                <div className="rounded-lg border border-status-error/30 bg-status-error/5 px-3 py-2 text-sm text-status-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={otp.length !== 6 || isVerifying}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 via-red-500 to-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-red-500/20 transition-all hover:shadow-xl hover:shadow-red-500/30 disabled:cursor-not-allowed disabled:opacity-60 active:scale-[0.98]"
              >
                {isVerifying ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Sign in <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>

              <div className="text-center text-xs text-ink-tertiary">
                {resendIn > 0 ? (
                  <span>Resend code in {resendIn}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={isSending}
                    className="font-medium text-ink-secondary underline-offset-4 hover:text-ink hover:underline disabled:opacity-60"
                  >
                    Resend code
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-ink-tertiary">
          Having trouble? Contact your organizer.
        </p>
      </div>
    </div>
  );
}
